import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    CheckIcon,
    XMarkIcon,
    CurrencyDollarIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    HomeIcon,
    TagIcon,
    DocumentTextIcon,
    FunnelIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface FinancialCategory {
    _id: string;
    nombre: string;
    tipo: 'ingreso' | 'gasto';
    descripcion?: string;
    color: string;
    icono: string;
}

interface Building {
    _id: string;
    nombre: string;
    ciudad: string;
}

interface Property {
    _id: string;
    numero: string;
    tipo: string;
    edificio: string;
}

interface FinancialTransaction {
    _id: string;
    fecha: string;
    tipo: 'ingreso' | 'gasto';
    categoria: FinancialCategory;
    descripcion: string;
    monto: number;
    edificio: Building;
    propiedad: Property;
    numeroReferencia?: string;
    validada: boolean;
    validadaPor?: {
        nombre: string;
        apellido: string;
    };
    fechaValidacion?: string;
    creadoPor: {
        nombre: string;
        apellido: string;
    };
    createdAt: string;
}

interface TransactionFormData {
    fecha: string;
    tipo: 'ingreso' | 'gasto';
    categoria: string;
    descripcion: string;
    monto: string;
    edificio: string;
    propiedad: string;
    numeroReferencia: string;
}

const FinancialManagement: React.FC = () => {
    const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
    const [categories, setCategories] = useState<FinancialCategory[]>([]);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState<FinancialTransaction | null>(null);
    const [formData, setFormData] = useState<TransactionFormData>({
        fecha: new Date().toISOString().split('T')[0],
        tipo: 'ingreso',
        categoria: '',
        descripcion: '',
        monto: '',
        edificio: '',
        propiedad: '',
        numeroReferencia: ''
    });

    // Filtros
    const [filters, setFilters] = useState({
        search: '',
        tipo: '',
        edificio: '',
        propiedad: '',
        categoria: '',
        fechaInicio: '',
        fechaFin: ''
    });

    // Paginación
    const [pagination, setPagination] = useState({
        current: 1,
        pages: 1,
        total: 0,
        limit: 20
    });

    useEffect(() => {
        fetchData();
    }, [pagination.current, filters]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            // Construir parámetros de consulta
            const params = new URLSearchParams();
            params.append('page', pagination.current.toString());
            params.append('limit', pagination.limit.toString());
            
            if (filters.search) params.append('search', filters.search);
            if (filters.tipo) params.append('tipo', filters.tipo);
            if (filters.edificio) params.append('edificio', filters.edificio);
            if (filters.propiedad) params.append('propiedad', filters.propiedad);
            if (filters.categoria) params.append('categoria', filters.categoria);
            if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
            if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);

            const [transactionsRes, categoriesRes, buildingsRes] = await Promise.all([
                fetch(apiUrl(`/api/financial/transactions?${params}`), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(apiUrl('/api/financial/categories'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(apiUrl('/api/buildings'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (transactionsRes.ok) {
                const transactionsData = await transactionsRes.json();
                if (transactionsData.success && transactionsData.data) {
                    setTransactions(Array.isArray(transactionsData.data.transactions) ? transactionsData.data.transactions : []);
                    setPagination(transactionsData.data.pagination || { current: 1, pages: 1, total: 0, limit: 10 });
                } else {
                    console.error('Error: transactions data is invalid:', transactionsData);
                    setTransactions([]);
                }
            } else {
                console.error('Error fetching transactions:', transactionsRes.status, transactionsRes.statusText);
                setTransactions([]);
            }

            if (categoriesRes.ok) {
                const categoriesData = await categoriesRes.json();
                if (categoriesData.success && Array.isArray(categoriesData.data)) {
                    setCategories(categoriesData.data);
                } else {
                    console.error('Error: categories data is not an array:', categoriesData);
                    setCategories([]);
                }
            } else {
                console.error('Error fetching categories:', categoriesRes.status, categoriesRes.statusText);
                setCategories([]);
            }

            if (buildingsRes.ok) {
                const buildingsData = await buildingsRes.json();
                if (buildingsData.success && buildingsData.data) {
                    // La API devuelve data.buildings, no data directamente
                    const buildingsArray = buildingsData.data.buildings || buildingsData.data;
                    if (Array.isArray(buildingsArray)) {
                        setBuildings(buildingsArray);
                    } else {
                        console.error('Error: buildings data is not an array:', buildingsData);
                        setBuildings([]);
                    }
                } else {
                    console.error('Error: buildings data is invalid:', buildingsData);
                    setBuildings([]);
                }
            } else {
                console.error('Error fetching buildings:', buildingsRes.status, buildingsRes.statusText);
                setBuildings([]);
            }

        } catch (error) {
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProperties = async (buildingId: string) => {
        if (!buildingId) {
            setFilteredProperties([]);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/properties?edificio=${buildingId}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    // La API devuelve data.properties, no data directamente
                    const propertiesArray = data.data.properties || data.data;
                    if (Array.isArray(propertiesArray)) {
                        setFilteredProperties(propertiesArray);
                    } else {
                        console.error('Error: properties data is not an array:', data);
                        setFilteredProperties([]);
                    }
                } else {
                    console.error('Error: properties data is invalid:', data);
                    setFilteredProperties([]);
                }
            } else {
                console.error('Error fetching properties:', response.status, response.statusText);
                setFilteredProperties([]);
            }
        } catch (error) {
            console.error('Error al cargar propiedades:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            const token = localStorage.getItem('token');
            const url = editingTransaction 
                ? apiUrl(`/api/financial/transactions/${editingTransaction._id}`)
                : apiUrl('/api/financial/transactions');
            const method = editingTransaction ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                await fetchData();
                setShowModal(false);
                setEditingTransaction(null);
                resetForm();
            } else {
                const error = await response.json();
                alert(error.message || 'Error al guardar transacción');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar transacción');
        }
    };

    const handleEdit = (transaction: FinancialTransaction) => {
        setEditingTransaction(transaction);
        setFormData({
            fecha: new Date(transaction.fecha).toISOString().split('T')[0],
            tipo: transaction.tipo,
            categoria: transaction.categoria._id,
            descripcion: transaction.descripcion,
            monto: transaction.monto.toString(),
            edificio: transaction.edificio._id,
            propiedad: transaction.propiedad._id,
            numeroReferencia: transaction.numeroReferencia || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta transacción?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/financial/transactions/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchData();
            } else {
                alert('Error al eliminar transacción');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar transacción');
        }
    };

    const handleValidate = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/financial/transactions/${id}/validate`), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchData();
            } else {
                alert('Error al validar transacción');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al validar transacción');
        }
    };

    const resetForm = () => {
        setFormData({
            fecha: new Date().toISOString().split('T')[0],
            tipo: 'ingreso',
            categoria: '',
            descripcion: '',
            monto: '',
            edificio: '',
            propiedad: '',
            numeroReferencia: ''
        });
    };

    const handleNewTransaction = () => {
        setEditingTransaction(null);
        resetForm();
        setShowModal(true);
    };

    const handleBuildingChange = (buildingId: string) => {
        setFormData({ ...formData, edificio: buildingId, propiedad: '' });
        fetchProperties(buildingId);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES');
    };

    const getTipoColor = (tipo: string) => {
        return tipo === 'ingreso' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    };

    const getTipoLabel = (tipo: string) => {
        return tipo === 'ingreso' ? 'Ingreso' : 'Gasto';
    };

    const filteredCategories = categories.filter(cat => cat.tipo === formData.tipo);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hotel-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Gestión Financiera</h2>
                    <p className="text-gray-600">Administra ingresos y gastos por propiedad</p>
                </div>
                <button
                    onClick={handleNewTransaction}
                    className="bg-hotel-600 text-white px-4 py-2 rounded-lg hover:bg-hotel-700 flex items-center justify-center space-x-2"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span>Nueva Transacción</span>
                </button>
            </div>

            {/* Filtros */}
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Buscar
                        </label>
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                placeholder="Buscar transacciones..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo
                        </label>
                        <select
                            value={filters.tipo}
                            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        >
                            <option value="">Todos los tipos</option>
                            <option value="ingreso">Ingresos</option>
                            <option value="gasto">Gastos</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Edificio
                        </label>
                        <select
                            value={filters.edificio}
                            onChange={(e) => setFilters({ ...filters, edificio: e.target.value, propiedad: '' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        >
                            <option value="">Todos los edificios</option>
                            {Array.isArray(buildings) && buildings.map((building) => (
                                <option key={building._id} value={building._id}>
                                    {building.nombre} - {building.ciudad}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoría
                        </label>
                        <select
                            value={filters.categoria}
                            onChange={(e) => setFilters({ ...filters, categoria: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        >
                            <option value="">Todas las categorías</option>
                            {Array.isArray(categories) && categories.map((category) => (
                                <option key={category._id} value={category._id}>
                                    {category.nombre} ({category.tipo})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fecha Inicio
                        </label>
                        <input
                            type="date"
                            value={filters.fechaInicio}
                            onChange={(e) => setFilters({ ...filters, fechaInicio: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fecha Fin
                        </label>
                        <input
                            type="date"
                            value={filters.fechaFin}
                            onChange={(e) => setFilters({ ...filters, fechaFin: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        />
                    </div>
                </div>
            </div>

            {/* Tabla de transacciones */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Categoría
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Descripción
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Monto
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Propiedad
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Array.isArray(transactions) && transactions.map((transaction) => (
                                <tr key={transaction._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDate(transaction.fecha)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoColor(transaction.tipo)}`}>
                                            {getTipoLabel(transaction.tipo)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <div 
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: transaction.categoria.color }}
                                            ></div>
                                            <span className="text-sm text-gray-900">{transaction.categoria.nombre}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                        {transaction.descripcion}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <span className={transaction.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}>
                                            {transaction.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(transaction.monto)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div>
                                            <div className="font-medium">{transaction.propiedad.numero}</div>
                                            <div className="text-gray-500">{transaction.edificio.nombre}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {transaction.validada ? (
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-green-800 bg-green-100">
                                                <CheckIcon className="h-3 w-3 mr-1" />
                                                Validada
                                            </span>
                                        ) : (
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-yellow-800 bg-yellow-100">
                                                <XMarkIcon className="h-3 w-3 mr-1" />
                                                Pendiente
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <button
                                            onClick={() => handleEdit(transaction)}
                                            className="text-hotel-600 hover:text-hotel-900"
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        {!transaction.validada && (
                                            <button
                                                onClick={() => handleValidate(transaction._id)}
                                                className="text-green-600 hover:text-green-900"
                                                title="Validar transacción"
                                            >
                                                <CheckIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDelete(transaction._id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginación */}
                {pagination.pages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPagination({ ...pagination, current: Math.max(1, pagination.current - 1) })}
                                disabled={pagination.current === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <button
                                onClick={() => setPagination({ ...pagination, current: Math.min(pagination.pages, pagination.current + 1) })}
                                disabled={pagination.current === pagination.pages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Siguiente
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Mostrando{' '}
                                    <span className="font-medium">{(pagination.current - 1) * pagination.limit + 1}</span>
                                    {' '}a{' '}
                                    <span className="font-medium">
                                        {Math.min(pagination.current * pagination.limit, pagination.total)}
                                    </span>
                                    {' '}de{' '}
                                    <span className="font-medium">{pagination.total}</span>
                                    {' '}resultados
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setPagination({ ...pagination, current: Math.max(1, pagination.current - 1) })}
                                        disabled={pagination.current === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                        const page = i + 1;
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setPagination({ ...pagination, current: page })}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                    pagination.current === page
                                                        ? 'z-10 bg-hotel-50 border-hotel-500 text-hotel-600'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setPagination({ ...pagination, current: Math.min(pagination.pages, pagination.current + 1) })}
                                        disabled={pagination.current === pagination.pages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Siguiente
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de transacción */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    {editingTransaction ? 'Editar Transacción' : 'Nueva Transacción'}
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Fecha *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.fecha}
                                            onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Tipo *
                                        </label>
                                        <select
                                            required
                                            value={formData.tipo}
                                            onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'ingreso' | 'gasto', categoria: '' })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                        >
                                            <option value="ingreso">Ingreso</option>
                                            <option value="gasto">Gasto</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Categoría *
                                    </label>
                                    <select
                                        required
                                        value={formData.categoria}
                                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                    >
                                        <option value="">Seleccionar categoría</option>
                                        {Array.isArray(filteredCategories) && filteredCategories.map((category) => (
                                            <option key={category._id} value={category._id}>
                                                {category.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Descripción *
                                    </label>
                                    <textarea
                                        required
                                        value={formData.descripcion}
                                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                        rows={3}
                                        placeholder="Descripción detallada de la transacción"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Monto *
                                    </label>
                                    <div className="relative">
                                        <CurrencyDollarIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="number"
                                            required
                                            min="0.01"
                                            step="0.01"
                                            value={formData.monto}
                                            onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Edificio *
                                        </label>
                                        <select
                                            required
                                            value={formData.edificio}
                                            onChange={(e) => handleBuildingChange(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                        >
                                            <option value="">Seleccionar edificio</option>
                                            {Array.isArray(buildings) && buildings.map((building) => (
                                                <option key={building._id} value={building._id}>
                                                    {building.nombre} - {building.ciudad}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Propiedad *
                                        </label>
                                        <select
                                            required
                                            value={formData.propiedad}
                                            onChange={(e) => setFormData({ ...formData, propiedad: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                            disabled={!formData.edificio}
                                        >
                                            <option value="">Seleccionar propiedad</option>
                                            {Array.isArray(filteredProperties) && filteredProperties.map((property) => (
                                                <option key={property._id} value={property._id}>
                                                    {property.numero} ({property.tipo})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Número de Referencia
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.numeroReferencia}
                                        onChange={(e) => setFormData({ ...formData, numeroReferencia: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                        placeholder="Número de factura, recibo, etc."
                                    />
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-hotel-600 text-white rounded-lg hover:bg-hotel-700"
                                    >
                                        {editingTransaction ? 'Actualizar' : 'Crear'} Transacción
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancialManagement;
